import type {
  ListSiteBranchesResponse,
  SiteBranchChangeSummary,
} from '@packages/domains/change-coordination/branches/data-types';
import {
  SITE_BRANCH_CHANGES_FETCH_REQUESTED,
  SITE_BRANCH_CHANGES_MARKED_STALE,
  SITE_BRANCH_CHANGES_RECEIVED_REMOTELY,
  SITE_BRANCH_CREATE_FAILED,
  SITE_BRANCH_CREATE_STARTED,
  SITE_BRANCH_CREATE_SUCCEEDED,
  SITE_BRANCH_DELETE_FAILED,
  SITE_BRANCH_DELETE_REDIRECT_COMPLETED,
  SITE_BRANCH_DELETE_REDIRECT_OPENED,
  SITE_BRANCH_DELETE_STARTED,
  SITE_BRANCH_DELETE_SUCCEEDED,
  SITE_BRANCH_DETAILS_UPDATED,
  SITE_BRANCH_ENTERED,
  SITE_BRANCH_EXITED,
  SITE_BRANCH_MERGE_CANCELLED,
  SITE_BRANCH_MERGE_CHECK_PASSED,
  SITE_BRANCH_MERGE_CONFLICTED,
  SITE_BRANCH_MERGE_FAILED,
  SITE_BRANCH_MERGE_INITIATED,
  SITE_BRANCH_MERGED_REMOTELY,
  SITE_BRANCH_MERGE_RESOLUTIONS_SUBMITTED,
  SITE_BRANCH_MERGE_STARTED,
  SITE_BRANCH_MERGE_SUCCEEDED,
  SITE_BRANCH_SWITCH_FAILED,
  SITE_BRANCH_SWITCH_STARTED,
  SITE_BRANCH_UPDATE_AVAILABLE,
  SITE_BRANCH_UPDATE_CANCELLED,
  SITE_BRANCH_UPDATE_CONFLICTED,
  SITE_BRANCH_UPDATE_FAILED,
  SITE_BRANCH_UPDATE_INITIATED,
  SITE_BRANCH_UPDATE_RESOLUTIONS_SUBMITTED,
  SITE_BRANCH_UPDATE_SKIPPED,
  SITE_BRANCH_UPDATE_STARTED,
  SITE_BRANCH_UPDATE_SUCCEEDED,
  SITE_BRANCHES_FETCH_FAILED,
  SITE_BRANCHES_FETCH_REQUESTED,
  SITE_BRANCHES_FETCH_SUCCEEDED,
} from '@packages/systems/designer/actions';
import {createAction, type Action} from '@packages/utilities/create-action';

import type {
  SiteBranchConflictType,
  SiteBranchErrorAction,
  SiteBranchUpdateIntent,
} from './types';

export type SiteBranchCreateRequestArgs = {
  name: string;
  description?: string;
  /** Main-site page the user was on; server maps it to the enter destination. */
  originPageId?: string;
  /** Defaults to entering the new branch; false keeps the current context. */
  enterAfterCreate?: boolean;
};

export const siteBranchCreateStarted = createAction<
  typeof SITE_BRANCH_CREATE_STARTED,
  {
    requestId: string;
    userId: string | null;
    requestArgs: SiteBranchCreateRequestArgs;
  }
>(SITE_BRANCH_CREATE_STARTED);

export const siteBranchCreateSucceeded = createAction<
  typeof SITE_BRANCH_CREATE_SUCCEEDED,
  {
    requestId: string;
    branchId: string;
    destinationPageId: string;
  }
>(SITE_BRANCH_CREATE_SUCCEEDED);

export const siteBranchCreateFailed = createAction<
  typeof SITE_BRANCH_CREATE_FAILED,
  {
    requestId: string;
  } & SiteBranchErrorAction
>(SITE_BRANCH_CREATE_FAILED);

export type SiteBranchSwitchArgs = {
  /** Target release id, or null to return to Main. */
  branchId: string | null;
  /**
   * Preferred landing page: create's API `destinationPageId`, or a deep-link
   * `pageId`. Used when it matches a working page `_id`, otherwise treated as
   * a Main-page id for `branchMainPageId` mapping.
   */
  destinationPageId?: string;
  /**
   * When true (boot / deep link), clear `?branchId=` if enter fails so
   * Designer is not left half-switched. Create omits this because the URL is
   * only written after a successful enter.
   */
  rollbackUrlOnFailure?: boolean;
  /**
   * Release name for the switch. Also gates the "Now working in …"
   * confirmation: boot and deep-link restores omit it so initial context setup
   * does not show a toast.
   */
  branchName?: string;
  /** Whether the release was just created and should use creation success feedback. */
  isNewBranch?: boolean;
  /** Exit only: skip the "Now working in Base site" toast; failures still surface. */
  suppressSuccessToast?: boolean;
  /** Enter only: open the release's review once the switch succeeds. */
  shouldOpenReview?: boolean;
};

/** Marks a local site-branch enter or exit as in flight. */
export const siteBranchSwitchStarted = createAction<
  typeof SITE_BRANCH_SWITCH_STARTED,
  SiteBranchSwitchArgs
>(SITE_BRANCH_SWITCH_STARTED);

/** Clears in-flight switch state after an enter or exit failure. */
export const siteBranchSwitchFailed = createAction<
  typeof SITE_BRANCH_SWITCH_FAILED,
  {branchId: string | null} & SiteBranchErrorAction
>(SITE_BRANCH_SWITCH_FAILED);

/** Marks Designer as working in a site branch after a successful enter. */
export const siteBranchEntered = createAction<
  typeof SITE_BRANCH_ENTERED,
  {
    branchId: string;
    /** Drop pageId/itemId that may have been added while entering. */
    shouldClearPageId?: boolean;
    /** Known release name (e.g. from create) before the list API includes this branch. */
    branchName?: string;
  }
>(SITE_BRANCH_ENTERED);

/** Asks the delete engine to delete one release. */
export const siteBranchDeleteStarted = createAction<
  typeof SITE_BRANCH_DELETE_STARTED,
  {branchId: string; suppressSuccessToast?: boolean}
>(SITE_BRANCH_DELETE_STARTED);

/** Clears in-flight delete state after the request fails. */
export const siteBranchDeleteFailed = createAction<
  typeof SITE_BRANCH_DELETE_FAILED,
  {branchId: string} & SiteBranchErrorAction
>(SITE_BRANCH_DELETE_FAILED);

/**
 * Drops one release from the loaded list once the server has deleted it.
 *
 * This is the whole success path: it ends the in-flight delete as well, so
 * there is no separate "delete finished" action to pair with the started one.
 */
export const siteBranchDeleteSucceeded = createAction<
  typeof SITE_BRANCH_DELETE_SUCCEEDED,
  {branchId: string}
>(SITE_BRANCH_DELETE_SUCCEEDED);

/**
 * Opens the "Release deleted" modal for a collaborator inside a release that
 * someone else deleted. `userId` is the deleter, or null when MPS didn't say.
 */
export const siteBranchDeleteRedirectOpened = createAction<
  typeof SITE_BRANCH_DELETE_REDIRECT_OPENED,
  {branchId: string; userId: string | null}
>(SITE_BRANCH_DELETE_REDIRECT_OPENED);

/** Closes the "Release deleted" modal once Continue has taken the user out. */
export const siteBranchDeleteRedirectCompleted = createAction<
  typeof SITE_BRANCH_DELETE_REDIRECT_COMPLETED
>(SITE_BRANCH_DELETE_REDIRECT_COMPLETED);

/** Marks Designer as back on Main after leaving a site branch. */
export const siteBranchExited = createAction<
  typeof SITE_BRANCH_EXITED,
  {shouldClearPageId?: boolean} | undefined
>(SITE_BRANCH_EXITED);

/** Optional conflict resolutions for a site-branch update request. */
export type SiteBranchUpdateRequestArgs = {
  resolutions?: Record<
    string,
    {
      id: string;
      resolution: 'main' | 'branch';
      type: 'style' | 'component';
    }
  >;
};

export const siteBranchUpdateInitiated = createAction<
  typeof SITE_BRANCH_UPDATE_INITIATED,
  {
    branchId: string;
    userId: string | null;
    /**
     * `merge`: preflight for merge — UpdateEngine checks, then opens the merge
     * modal when clear. Omit or `update` for standalone Check for updates.
     */
    intent?: SiteBranchUpdateIntent;
  }
>(SITE_BRANCH_UPDATE_INITIATED);

export const siteBranchUpdateAvailable = createAction<
  typeof SITE_BRANCH_UPDATE_AVAILABLE,
  {
    branchId: string;
  }
>(SITE_BRANCH_UPDATE_AVAILABLE);

export const siteBranchUpdateStarted = createAction<
  typeof SITE_BRANCH_UPDATE_STARTED,
  {
    branchId: string;
    userId: string | null;
    requestArgs?: SiteBranchUpdateRequestArgs;
  }
>(SITE_BRANCH_UPDATE_STARTED);

export const siteBranchUpdateSucceeded = createAction<
  typeof SITE_BRANCH_UPDATE_SUCCEEDED,
  {
    branchId: string;
  }
>(SITE_BRANCH_UPDATE_SUCCEEDED);

export const siteBranchUpdateFailed = createAction<
  typeof SITE_BRANCH_UPDATE_FAILED,
  {
    branchId: string;
  } & SiteBranchErrorAction
>(SITE_BRANCH_UPDATE_FAILED);

export const siteBranchUpdateConflicted = createAction<
  typeof SITE_BRANCH_UPDATE_CONFLICTED,
  {
    branchId: string;
    styles: Array<SiteBranchConflictType>;
    symbols: Array<SiteBranchConflictType>;
  }
>(SITE_BRANCH_UPDATE_CONFLICTED);

export const siteBranchUpdateSkipped = createAction<
  typeof SITE_BRANCH_UPDATE_SKIPPED,
  {
    branchId: string;
  }
>(SITE_BRANCH_UPDATE_SKIPPED);

export const siteBranchUpdateCancelled = createAction<
  typeof SITE_BRANCH_UPDATE_CANCELLED,
  {
    branchId: string;
  }
>(SITE_BRANCH_UPDATE_CANCELLED);

export const siteBranchUpdateResolutionsSubmitted = createAction<
  typeof SITE_BRANCH_UPDATE_RESOLUTIONS_SUBMITTED,
  {
    branchId: string;
    userId: string | null;
    resolutions: NonNullable<SiteBranchUpdateRequestArgs['resolutions']>;
  }
>(SITE_BRANCH_UPDATE_RESOLUTIONS_SUBMITTED);

export const siteBranchDetailsUpdated = createAction<
  typeof SITE_BRANCH_DETAILS_UPDATED,
  {
    branchId: string;
    name: string;
    description: string | null;
  }
>(SITE_BRANCH_DETAILS_UPDATED);

/** Optional conflict resolutions carried on a site-branch merge request. */
export type SiteBranchMergeRequestArgs = {
  resolutions?: NonNullable<SiteBranchUpdateRequestArgs['resolutions']>;
};

export const siteBranchMergeInitiated = createAction<
  typeof SITE_BRANCH_MERGE_INITIATED,
  {
    branchId: string;
    userId: string | null;
  }
>(SITE_BRANCH_MERGE_INITIATED);

/** Preflight clear — transition merge `checking` → `preparing` (open modal). */
export const siteBranchMergeCheckPassed = createAction<
  typeof SITE_BRANCH_MERGE_CHECK_PASSED,
  {
    branchId: string;
  }
>(SITE_BRANCH_MERGE_CHECK_PASSED);

export const siteBranchMergeStarted = createAction<
  typeof SITE_BRANCH_MERGE_STARTED,
  {
    branchId: string;
    userId: string | null;
  }
>(SITE_BRANCH_MERGE_STARTED);

export const siteBranchMergeSucceeded = createAction<
  typeof SITE_BRANCH_MERGE_SUCCEEDED,
  {
    branchId: string;
  }
>(SITE_BRANCH_MERGE_SUCCEEDED);

export const siteBranchMergeConflicted = createAction<
  typeof SITE_BRANCH_MERGE_CONFLICTED,
  {
    branchId: string;
    styles: Array<SiteBranchConflictType>;
    symbols: Array<SiteBranchConflictType>;
  }
>(SITE_BRANCH_MERGE_CONFLICTED);

export const siteBranchMergeFailed = createAction<
  typeof SITE_BRANCH_MERGE_FAILED,
  {
    branchId: string;
  } & SiteBranchErrorAction
>(SITE_BRANCH_MERGE_FAILED);

export const siteBranchMergeCancelled = createAction<
  typeof SITE_BRANCH_MERGE_CANCELLED,
  {
    branchId: string;
  }
>(SITE_BRANCH_MERGE_CANCELLED);

export const siteBranchMergeResolutionsSubmitted = createAction<
  typeof SITE_BRANCH_MERGE_RESOLUTIONS_SUBMITTED,
  {
    branchId: string;
    userId: string | null;
    resolutions: NonNullable<SiteBranchMergeRequestArgs['resolutions']>;
  }
>(SITE_BRANCH_MERGE_RESOLUTIONS_SUBMITTED);

/** Another session is merging the release this session is in. */
export const siteBranchMergedRemotely = createAction<
  typeof SITE_BRANCH_MERGED_REMOTELY,
  {
    branchId: string;
    userId: string | null;
  }
>(SITE_BRANCH_MERGED_REMOTELY);

export type SiteBranchesFetchRequestArgs = {
  /** Opt-in only. Omitted or false skips freshness computation entirely. */
  includeFreshness?: boolean;
};

/**
 * Requests the site's in-progress site branches. A bare call
 * (`siteBranchesFetchRequested()`) stays the cheap, freshness-free request;
 * pass `{includeFreshness: true}` only from a caller that needs it.
 */
export function siteBranchesFetchRequested(
  payload?: SiteBranchesFetchRequestArgs
): Action<
  typeof SITE_BRANCHES_FETCH_REQUESTED,
  SiteBranchesFetchRequestArgs | undefined
> {
  return {type: SITE_BRANCHES_FETCH_REQUESTED, payload};
}

export const siteBranchesFetchSucceeded = createAction<
  typeof SITE_BRANCHES_FETCH_SUCCEEDED,
  {response: ListSiteBranchesResponse}
>(SITE_BRANCHES_FETCH_SUCCEEDED);

export const siteBranchesFetchFailed = createAction<
  typeof SITE_BRANCHES_FETCH_FAILED
>(SITE_BRANCHES_FETCH_FAILED);

/** Requests one release's changes. Retrying dispatches it again. */
export const siteBranchChangesFetchRequested = createAction<
  typeof SITE_BRANCH_CHANGES_FETCH_REQUESTED,
  {siteBranchId: string}
>(SITE_BRANCH_CHANGES_FETCH_REQUESTED);

/**
 * Flags a release's loaded changes as out of date, keeping them on screen.
 * `useSiteBranchChangesFetch` requests them again once no request is in flight.
 */
export const siteBranchChangesMarkedStale = createAction<
  typeof SITE_BRANCH_CHANGES_MARKED_STALE,
  {siteBranchId: string}
>(SITE_BRANCH_CHANGES_MARKED_STALE);

export const siteBranchChangesReceivedRemotely = createAction<
  typeof SITE_BRANCH_CHANGES_RECEIVED_REMOTELY,
  {siteBranchId: string; changeSummary: SiteBranchChangeSummary}
>(SITE_BRANCH_CHANGES_RECEIVED_REMOTELY);
